import { LoadingOutlined } from '@ant-design/icons';
import { Skeleton, Spin } from 'antd';
import dayjs from 'dayjs';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { debounce } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntercom } from 'react-use-intercom';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, CrossSvg, SearchSvg } from '../assets/icons';
import AnswerSvg from '../assets/icons/AnswerSvg';
import ArrowRightSvg from '../assets/icons/ArrowRightSvg';
import comingSoon from '../assets/images/comingSoon.png';
import theme from '../assets/theme';
import { Button } from '../components/_atoms/Button';
import HomepageFilterSelect from '../components/_atoms/HomepageFilterSelect/HomepageFilterSelect';
import { IconWrapper } from '../components/_atoms/IconWrapper';
import { GeneratorCard } from '../components/_molecules/GeneratorCard';
import { HorisontalGeneratorCard } from '../components/_molecules/HorisontalGeneratorCard';
import { PresentationCard } from '../components/_molecules/PresentationCard';
import NewAppLayout from '../components/layouts/NewAppLayout';
import { useBreakPoint } from '../hooks/useBreakPoint';
import useGames from '../hooks/useGames';
import useGeneratorsQueryState, {
  SearchReducerstate,
} from '../hooks/useGeneratorsQueryState';
import { useGetGeneratorsById } from '../hooks/useGetGeneratorsById';
import { useLatestGenerators } from '../hooks/useLatestGenerators';
import { useMetrics } from '../hooks/useMetrics';
import usePaywallPrompt from '../hooks/usePaywallPrompt';
import { useQueryGenerators } from '../hooks/useQueryGenerators';
import useRecentGenerators from '../hooks/useRecentGenerators';
import useRecentTemplates from '../hooks/useRecentTemplates';
import { useRecommendedGenerators } from '../hooks/useRecommendedGenerators';
import useRoutePrefetch from '../hooks/useRoutePrefetch';
import useRouterMatch from '../hooks/useRouterMatch';
import useTypewriter from '../hooks/useTypewriter';
import { getGeneratorsUrl } from '../services/DiscoverService/discoverUtils';
import { ContentCategory, subjectOptions } from '../utils/authenticatedHomeUtils';
import { CURIPOD_FILTER_GRADE_OPTIONS } from '../utils/general';
import { getConfig } from '../utils/store';
import { getTitle } from '../utils/templateUtils';
import { TenantLicense } from '../utils/tenantPlanUtils';
import CountrySchoolSelectionPopup from './CountrySchoolSelectionPopup';
import { NewMainContainerWithGeneratorForm } from './generators/[seoTitle]/g/[generatorId]';
import TrialWelcomeMessagePopup from './TrialWelcomeMessagePopup';

const DiscoveryRowContainer = styled.div`
  margin-bottom: 30px;

  @media (max-width: 800px) {
    margin-bottom: 30px;
  }
`;

const FilterButton = ({
  handleSelectCategory,
  index,
  icon,
  isActive,
  categoryId,
  categoryTitle,
}: {
  index: number;
  handleSelectCategory: (categoryId: string, key: number) => void;
  icon: React.ReactElement;
  categoryTitle: string;
  isActive: boolean;
  categoryId: string;
}) => {
  const animation = useAnimation();
  const hoverSequence = async () => {
    await animation.start({ scale: 1.03, transition: { duration: 0.1 } });
    animation.start({ scale: 1, transition: { duration: 0.1 } });
  };

  const isDesktop = useBreakPoint(800);

  return (
    <AnimatePresence>
      <motion.div
        animate={animation}
        onHoverStart={hoverSequence}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <StyledFilterButton
          key={index}
          $isActive={isActive}
          onClick={() => {
            handleSelectCategory(categoryId, index);
          }}
          icon={isDesktop ? <ButtonIconWrapper>{icon}</ButtonIconWrapper> : undefined}
        >
          {categoryTitle}
        </StyledFilterButton>
      </motion.div>
    </AnimatePresence>
  );
};

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.black};
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  text-transform: uppercase;
  padding-left: 30px;

  @media (max-width: 800px) {
    padding-left: 20px;
    margin-bottom: 0px;
  }

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 2}px;
  }
`;

const RecentGeneratorsSlidingGrid = ({ title }: { title: string }) => {
  const { generators, isFetchingGenerators } = useRecentGenerators();
  const { tenantPlan } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [generators]);

  if (generators?.length === 0) return null;

  return (
    <DiscoveryRowContainer>
      <Title $fontSize={16}>{title}</Title>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowLeftSvg />}
                notext={true}
                onClick={() => {
                  handleLeftArrowClicked();
                }}
              />
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {generators?.map(aiGenerator => {
            return (
              <Link
                href={getGeneratorsUrl({
                  title: aiGenerator.title,
                  generatorId: aiGenerator.id,
                })}
                key={aiGenerator.id}
              >
                <a style={{ height: '100%', width: 'fit-content' }}>
                  <HorisontalGeneratorCard
                    coverImageId={aiGenerator.coverImageId}
                    firstPublished={aiGenerator.firstPublished}
                    title={aiGenerator.title || ''}
                    status={aiGenerator.status}
                    isPremiumGenerator={aiGenerator.aiContent?.isPremiumGenerator}
                    generatorPlan={aiGenerator.aiContent?.generatorPlan}
                    tenantPlan={tenantPlan}
                    description={aiGenerator.description || undefined}
                    learningStandards={aiGenerator.learningStandards}
                  />
                </a>
              </Link>
            );
          })}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
        </HorisontalScrollContainer>
        {showRightArrow && generators && generators.length > 2 && (
          <GeneratorsRightArrowContainer>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowRightSvg />}
                notext={true}
                onClick={() => {
                  handleRightArrowClicked();
                }}
              />
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </DiscoveryRowContainer>
  );
};

const GeneratorSearchEmptyState = () => {
  const config = getConfig();
  const { generators, isFetchingGenerators } = useGetGeneratorsById(
    config.discovery.fullLessonIds,
  );
  const { showNewMessages } = useIntercom();
  const router = useRouter();
  const routerQuery = router.query;
  //get the search phrase from the query if it exists under searchPhrase
  const searchPhrase = routerQuery.searchPhrase as string;

  return (
    <div>
      {isFetchingGenerators && !generators && (
        <ResponsiveGeneratorsGridContainer
          style={{
            width: '100%',
            maxHeight: 180,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 100, color: theme.colors.orange }}
                spin
              />
            }
          />
        </ResponsiveGeneratorsGridContainer>
      )}
      {generators?.[0] && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            padding: '20px',
          }}
        >
          <h1>One customizable lesson found</h1>
          <NewMainContainerWithGeneratorForm
            authenticated={true}
            generator={generators?.[0]}
            searchPhrase={searchPhrase}
          />
          <span
            style={{
              marginTop: 20,
              fontWeight: 'bold',
              fontSize: 16,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
            onClick={() => showNewMessages()}
          >
            I would love to see a specific customizable lesson for this{' '}
          </span>
        </div>
      )}
    </div>
  );
};

const SearchGeneratorsGrid = ({
  tenantPlan,
  searchState,
}: {
  tenantPlan?: TenantLicense;
  searchState: SearchReducerstate;
}) => {
  const isDesktop = useBreakPoint(800);

  const {
    generators: generatorsFromSearch,
    isFetchingGenerators,
    isFetchingNextPageGenerators,
    fetchNextPageGenerators,
    hasNextPageGenerators,
    total,
  } = useQueryGenerators({ searchState });

  const config = getConfig();
  const { generators: didntFindWhatYouWereLookingForGenerators } = useGetGeneratorsById(
    config.discovery.didNotFindTryTheseSuggestionIds,
  );

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 150) {
        !isFetchingNextPageGenerators &&
          hasNextPageGenerators &&
          fetchNextPageGenerators();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [fetchNextPageGenerators, hasNextPageGenerators, isFetchingNextPageGenerators]);

  return (
    <>
      {generatorsFromSearch.length === 0 && !isFetchingGenerators && (
        <GeneratorSearchEmptyState />
      )}
      {!isFetchingGenerators && generatorsFromSearch.length !== 0 && (
        <h3 style={{ padding: '0px 20px', margin: 0 }}>
          {total} customizable lesson{generatorsFromSearch.length > 1 && 's'}
        </h3>
      )}
      {isFetchingGenerators && generatorsFromSearch.length === 0 && (
        <ResponsiveGeneratorsGridContainer
          style={{
            width: '100%',
            maxHeight: 180,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 100, color: theme.colors.orange }}
                spin
              />
            }
          />
        </ResponsiveGeneratorsGridContainer>
      )}
      <ResponsiveGeneratorsGridContainer>
        {generatorsFromSearch.map(generator => {
          return (
            <Link
              href={getGeneratorsUrl({
                title: generator.title,
                generatorId: generator.id,
              })}
              key={generator.id}
            >
              <a style={{ height: '100%', width: '100%' }}>
                <GeneratorCard
                  subTitle=""
                  title={generator.title || ''}
                  coverImageId={generator.coverImageId}
                  status={generator.status}
                  firstPublished={generator.firstPublished}
                  isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                  generatorPlan={generator?.aiContent?.generatorPlan}
                  description={generator.description || ''}
                  tenantPlan={tenantPlan}
                  learningStandards={generator.learningStandards}
                />
              </a>
            </Link>
          );
        })}
      </ResponsiveGeneratorsGridContainer>
      {isFetchingNextPageGenerators && (
        <ResponsiveGeneratorsGridContainer
          style={{
            width: '100%',
            maxHeight: 180,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 100, color: theme.colors.orange }}
                spin
              />
            }
          />
        </ResponsiveGeneratorsGridContainer>
      )}
      {generatorsFromSearch.length !== 0 && (
        <>
          <h1
            style={{ textAlign: 'center', marginTop: 20, fontSize: isDesktop ? 30 : 24 }}
          >
            Did you not find what you were looking for?
          </h1>
          <h3 style={{ textAlign: 'center', fontSize: isDesktop ? 20 : 16 }}>
            You might want to try one of these customizable lessons below to create your
            next lesson!
          </h3>
          {!!didntFindWhatYouWereLookingForGenerators && (
            <ResponsiveGeneratorsGridContainer $isCentered={true}>
              {didntFindWhatYouWereLookingForGenerators.map(generator => {
                return (
                  <Link
                    href={getGeneratorsUrl({
                      title: generator.title,
                      generatorId: generator.id,
                    })}
                    key={generator.id}
                  >
                    <a style={{ height: '100%', width: '100%' }}>
                      <GeneratorCard
                        subTitle=""
                        title={generator.title || ''}
                        coverImageId={generator.coverImageId}
                        status={generator.status}
                        firstPublished={generator.firstPublished}
                        isPremiumGenerator={generator?.aiContent?.isPremiumGenerator}
                        description={generator.description || ''}
                        generatorPlan={generator?.aiContent?.generatorPlan}
                        tenantPlan={tenantPlan}
                        learningStandards={generator.learningStandards}
                      />
                    </a>
                  </Link>
                );
              })}
            </ResponsiveGeneratorsGridContainer>
          )}
        </>
      )}
    </>
  );
};

export const ResponsiveGeneratorsGridContainer = styled.div<{ $isCentered?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  padding: 20px;

  @media (max-width: 4096px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1440px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
  @media (max-width: 1280px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 950px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 800px) {
    // 800 we go into mobile screen mode
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }

  ${({ $isCentered }) =>
    $isCentered &&
    css`
      max-width: 500px;
      margin: 0 auto;
      @media (max-width: 4096px) {
        grid-template-columns: 1fr 1fr 1fr;
      }
      @media (max-width: 700px) {
        grid-template-columns: 1fr 1fr;
      }
    `}
`;

const GeneratorsSlidingGrid = ({
  category,
  title,
  tenantPlan,
  searchState,
}: {
  category: string;
  title: string;
  tenantPlan?: TenantLicense;
  searchState: SearchReducerstate;
}) => {
  const {
    generators: generatorsFromSearch,
    isFetchingGenerators,
    fetchNextPageGenerators,
    hasNextPageGenerators,
  } = useQueryGenerators({ preSetFilter: category, searchState });

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [generatorsFromSearch]);

  const { ref: scrollRef, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPageGenerators && !isFetchingGenerators) {
      fetchNextPageGenerators();
    }
  }, [inView, hasNextPageGenerators, isFetchingGenerators, fetchNextPageGenerators]);

  return (
    <DiscoveryRowContainer>
      <Title $fontSize={16}>{title}</Title>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowLeftSvg />}
                notext={true}
                onClick={() => {
                  handleLeftArrowClicked();
                }}
              />
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {generatorsFromSearch.length === 0 &&
            !isFetchingGenerators &&
            title.toLowerCase().includes('coming soon') && (
              <HorisontalGeneratorPlaceholderImg src={comingSoon.src} />
            )}
          {generatorsFromSearch?.map(aiGenerator => {
            return (
              <Link
                href={getGeneratorsUrl({
                  title: aiGenerator.title,
                  generatorId: aiGenerator.id,
                })}
                key={aiGenerator.id}
              >
                <a style={{ height: '100%', width: 'fit-content' }}>
                  <HorisontalGeneratorCard
                    coverImageId={aiGenerator.coverImageId}
                    title={aiGenerator.title || ''}
                    firstPublished={aiGenerator.firstPublished}
                    status={aiGenerator.status}
                    isPremiumGenerator={aiGenerator.aiContent?.isPremiumGenerator}
                    generatorPlan={aiGenerator.aiContent?.generatorPlan}
                    tenantPlan={tenantPlan}
                    description={aiGenerator.description || undefined}
                    learningStandards={aiGenerator.learningStandards}
                  />
                </a>
              </Link>
            );
          })}
          <div ref={scrollRef} />
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
          {isFetchingGenerators && <Skeleton.Button active={true} />}
        </HorisontalScrollContainer>
        {showRightArrow && generatorsFromSearch && generatorsFromSearch.length > 2 && (
          <GeneratorsRightArrowContainer>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowRightSvg />}
                notext={true}
                onClick={() => {
                  handleRightArrowClicked();
                }}
              />
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </DiscoveryRowContainer>
  );
};

const HorisontalGeneratorPlaceholderImg = styled.img`
  width: 250px;
  height: 150px;
  object-fit: cover;
  object-position: center;
  border-radius: 8px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  @media (max-width: 800px) {
    width: 200px;
    height: 120px;
  }
`;

const CustomGeneratorsSlidingGrid = ({ title }: { title: string }) => {
  const { generators, isFetchingGenerators } = useRecommendedGenerators();

  const { tenantPlan } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [generators]);

  return (
    <DiscoveryRowContainer>
      <Title $fontSize={16}>{title}</Title>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowLeftSvg />}
                notext={true}
                onClick={() => {
                  handleLeftArrowClicked();
                }}
              />
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {generators?.map(aiGenerator => {
            return (
              <Link
                href={getGeneratorsUrl({
                  title: aiGenerator.title,
                  generatorId: aiGenerator.id,
                })}
                key={aiGenerator.id}
              >
                <a style={{ height: '100%', width: 'fit-content' }}>
                  <HorisontalGeneratorCard
                    coverImageId={aiGenerator.coverImageId}
                    title={aiGenerator.title || ''}
                    status={aiGenerator.status}
                    firstPublished={aiGenerator.firstPublished}
                    isPremiumGenerator={aiGenerator.aiContent?.isPremiumGenerator}
                    generatorPlan={aiGenerator.aiContent?.generatorPlan}
                    tenantPlan={tenantPlan}
                    description={aiGenerator.description || undefined}
                    learningStandards={aiGenerator.learningStandards}
                  />
                </a>
              </Link>
            );
          })}
          {isFetchingGenerators && (
            <>
              <Skeleton.Button active={true} />
              <Skeleton.Button active={true} />
              <Skeleton.Button active={true} />
              <Skeleton.Button active={true} />
              <Skeleton.Button active={true} />
            </>
          )}
        </HorisontalScrollContainer>
        {showRightArrow && generators && generators.length > 2 && (
          <GeneratorsRightArrowContainer>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowRightSvg />}
                notext={true}
                onClick={() => {
                  handleRightArrowClicked();
                }}
              />
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </DiscoveryRowContainer>
  );
};

const LatestGeneratorsSlidingGrid = ({ title }: { title: string }) => {
  const { generators, isFetchingGenerators } = useLatestGenerators();
  const { tenantPlan } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [generators]);

  return (
    <DiscoveryRowContainer>
      <Title $fontSize={16}>{title}</Title>
      <ScrollMainContainer>
        {showLeftArrow && (
          <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowLeftSvg />}
                notext={true}
                onClick={() => {
                  handleLeftArrowClicked();
                }}
              />
            </div>
          </GeneratorsLeftArrowContainer>
        )}
        <HorisontalScrollContainer ref={listRef}>
          {generators?.map(aiGenerator => {
            return (
              <Link
                href={getGeneratorsUrl({
                  title: aiGenerator.title,
                  generatorId: aiGenerator.id,
                })}
                key={aiGenerator.id}
              >
                <a style={{ height: '100%', width: 'fit-content' }}>
                  <HorisontalGeneratorCard
                    coverImageId={aiGenerator.coverImageId}
                    title={aiGenerator.title || ''}
                    status={aiGenerator.status}
                    firstPublished={aiGenerator.firstPublished}
                    isPremiumGenerator={aiGenerator.aiContent?.isPremiumGenerator}
                    generatorPlan={aiGenerator.aiContent?.generatorPlan}
                    tenantPlan={tenantPlan}
                    description={aiGenerator.description || undefined}
                    learningStandards={aiGenerator.learningStandards}
                  />
                </a>
              </Link>
            );
          })}
          {isFetchingGenerators && (
            <>
              <Skeleton.Button active={true} />
              <Skeleton.Button active={true} />
              <Skeleton.Button active={true} />
              <Skeleton.Button active={true} />
              <Skeleton.Button active={true} />
            </>
          )}
        </HorisontalScrollContainer>
        {showRightArrow && generators && generators.length > 2 && (
          <GeneratorsRightArrowContainer>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                paddingBottom: 20,
              }}
            >
              <StyledArrowButton
                styles={{
                  background: theme.colors.white,
                  border: 'none',
                  height: 35,
                  width: 35,
                  borderRadius: '50%',
                  color: theme.colors.black,
                }}
                type="ghost"
                icon={<ArrowRightSvg />}
                notext={true}
                onClick={() => {
                  handleRightArrowClicked();
                }}
              />
            </div>
          </GeneratorsRightArrowContainer>
        )}
      </ScrollMainContainer>
    </DiscoveryRowContainer>
  );
};

const StyledArrowButton = styled(Button)`
  padding: 0px;
  svg {
    height: 28px;
    width: 28px;
  }
`;

const GeneratorsLeftArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 10px);
  width: 40px;
  left: 20px;
  top: 0;
  background: linear-gradient(
    to left,
    rgba(255, 255, 255, 0),
    ${theme.colors.background}
  );
  z-index: 1;
`;

const GeneratorsRightArrowContainer = styled.div`
  position: absolute;
  height: calc(100% - 10px);
  width: 40px;
  right: 20px;
  top: 0;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0),
    ${theme.colors.background}
  );
  z-index: 1;
`;

const ScrollMainContainer = styled.div`
  position: relative;
  width: 100%;
  height: 210px;
  padding: 0px 20px;
  @media (max-width: 800px) {
    height: 150px;
  }
`;

const HorisontalScrollContainer = styled.ul`
  width: 100%;
  height: 210px;
  overflow-x: auto;
  overflow-y: hidden;

  display: flex;
  gap: 10px;
  padding: 10px 0px 10px 0px; // padding bottom to contain scroll bar

  // Hide the horisontal scrollbars
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }

  .ant-skeleton,
  .ant-skeleton-button {
    height: 100%;
    aspect-ratio: 1.28;
    border-radius: 5px;
    min-width: unset;
    width: unset;
  }

  @media (max-width: 800px) {
    height: 150px;
  }
`;

const LessonsSlidingGrid = ({ title }: { title: string }) => {
  const { templates, isFetchingTemplates } = useRecentTemplates({ limit: 10 });
  const { tenantId } = useRouterMatch();
  const [hoveredTemplate, setHoveredTemplate] = useState<string | undefined>();
  const metrics = useMetrics();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);
    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [templates]);

  return (
    <DiscoveryRowContainer>
      {templates && templates.length >= 1 && (
        <>
          <Title $fontSize={16}>{title}</Title>
          <ScrollMainContainer>
            {showLeftArrow && (
              <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    paddingBottom: 20,
                  }}
                >
                  <StyledArrowButton
                    styles={{
                      background: theme.colors.white,
                      border: 'none',
                      height: 35,
                      width: 35,
                      borderRadius: '50%',
                      color: theme.colors.black,
                    }}
                    type="ghost"
                    icon={<ArrowLeftSvg />}
                    notext={true}
                    onClick={() => {
                      handleLeftArrowClicked();
                    }}
                  />
                </div>
              </GeneratorsLeftArrowContainer>
            )}
            <HorisontalScrollContainer ref={listRef}>
              {isFetchingTemplates && templates === undefined && (
                <>
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                </>
              )}
              {templates.map(template => {
                if (
                  template?.content?.slides &&
                  template.content.slides.length > 0 &&
                  template.content.slides[0]
                ) {
                  return (
                    <PresentationWrapper>
                      <Link href={`/${tenantId}/lessons/${template.id}`}>
                        <a
                          style={{ height: '100%', width: '100%' }}
                          onMouseEnter={() => setHoveredTemplate(template.id)}
                          onMouseLeave={() => setHoveredTemplate(undefined)}
                        >
                          <PresentationCard
                            isHovering={hoveredTemplate === template.id}
                            slides={template.content.slides}
                            subTitle={`Edited:
                        ${dayjs(template.modifiedAt).format('MMMM DD, YYYY - hh:mma')}`}
                            isGenerator={!!template.aiContent}
                            title={
                              template.title
                                ? template.title
                                : getTitle({
                                    title: template.title,
                                    slides:
                                      template.type === 'template'
                                        ? template.content.slides
                                        : null,
                                    maxLength: 100,
                                  })
                            }
                          />
                        </a>
                      </Link>
                      {hoveredTemplate === template.id && (
                        <Link href={`/${tenantId}/lessons/${template.id}/edit`}>
                          <a
                            style={{ height: '100%', width: '100%' }}
                            onMouseEnter={() => setHoveredTemplate(template.id)}
                            onMouseLeave={() => setHoveredTemplate(undefined)}
                          >
                            <EditButton
                              onClick={() => {
                                metrics.logEvent(
                                  'AuthenticatedHome.EditRecentLesson.Clicked',
                                  {
                                    templateId: template.id,
                                    tenantId,
                                  },
                                );
                              }}
                              notext
                              icon={<AnswerSvg />}
                            ></EditButton>
                          </a>
                        </Link>
                      )}
                    </PresentationWrapper>
                  );
                }
              })}
            </HorisontalScrollContainer>
            {showRightArrow && templates && templates.length > 2 && (
              <GeneratorsRightArrowContainer>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    paddingBottom: 20,
                  }}
                >
                  <StyledArrowButton
                    styles={{
                      background: theme.colors.white,
                      border: 'none',
                      height: 35,
                      width: 35,
                      borderRadius: '50%',
                      color: theme.colors.black,
                    }}
                    type="ghost"
                    icon={<ArrowRightSvg />}
                    notext={true}
                    onClick={() => {
                      handleRightArrowClicked();
                    }}
                  />
                </div>
              </GeneratorsRightArrowContainer>
            )}
          </ScrollMainContainer>
        </>
      )}
    </DiscoveryRowContainer>
  );
};

const PresentationWrapper = styled.div`
  height: 210px;
  width: 243px;
  max-width: 243px;
  min-width: 243px;
  position: relative;

  @media (max-width: 800px) {
    height: 150px;
    width: 166px;
    max-width: 166px;
    min-width: 166px;
  }
`;

const EditButton = styled(Button)`
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 2;
  padding: 2px 5px;

  background: #00000099;
  border-radius: 5px;
  color: ${theme.colors.white};
  font-size: 12px;
  height: 30px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;

  svg {
    height: 20px;
    width: 20px;
  }
`;

const ResultsSlidingGrid = ({ title }: { title: string }) => {
  const { games, isFetching } = useGames();
  const { tenantId } = useRouterMatch();

  const [hoveredGame, setHoveredGame] = useState<string | undefined>();

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const handleRightArrowClicked = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };
  const handleLeftArrowClicked = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;

      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [games]);

  return (
    <DiscoveryRowContainer>
      {games && games.length >= 1 && (
        <>
          <Title $fontSize={16}>{title}</Title>
          <ScrollMainContainer>
            {showLeftArrow && (
              <GeneratorsLeftArrowContainer onClick={handleLeftArrowClicked}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    paddingBottom: 20,
                  }}
                >
                  <StyledArrowButton
                    styles={{
                      background: theme.colors.white,
                      border: 'none',
                      height: 35,
                      width: 35,
                      borderRadius: '50%',
                      color: theme.colors.black,
                    }}
                    type="ghost"
                    icon={<ArrowLeftSvg />}
                    notext={true}
                    onClick={() => {
                      handleLeftArrowClicked();
                    }}
                  />
                </div>
              </GeneratorsLeftArrowContainer>
            )}
            <HorisontalScrollContainer ref={listRef}>
              {isFetching && (
                <>
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                  <Skeleton.Button active={true} />
                </>
              )}
              {games.map(game => {
                if (game?.slides && game.slides.length > 0 && game.slides[0]) {
                  return (
                    <PresentationWrapper>
                      <Link href={`/${tenantId}/reports/${game.id}`}>
                        <a
                          style={{ height: '100%', width: '100%' }}
                          onMouseEnter={() => setHoveredGame(game.id)}
                          onMouseLeave={() => setHoveredGame(undefined)}
                        >
                          <PresentationCard
                            isHovering={game.id === hoveredGame}
                            slides={game.slides}
                            subTitle={`Played: 
                            ${dayjs(game.lastPlayedAt).format('MMMM DD, YYYY - hh:mma')}`}
                            title={
                              game.title
                                ? game.title
                                : getTitle({
                                    title: game.title,
                                    slides: game.slides || null,
                                    maxLength: 100,
                                  })
                            }
                          />
                        </a>
                      </Link>
                    </PresentationWrapper>
                  );
                }
              })}
            </HorisontalScrollContainer>
            {showRightArrow && games && games.length > 2 && (
              <GeneratorsRightArrowContainer>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    paddingBottom: 20,
                  }}
                >
                  <StyledArrowButton
                    styles={{
                      background: theme.colors.white,
                      border: 'none',
                      height: 35,
                      width: 35,
                      borderRadius: '50%',
                      color: theme.colors.black,
                    }}
                    type="ghost"
                    icon={<ArrowRightSvg />}
                    notext={true}
                    onClick={() => {
                      handleRightArrowClicked();
                    }}
                  />
                </div>
              </GeneratorsRightArrowContainer>
            )}
          </ScrollMainContainer>
        </>
      )}
    </DiscoveryRowContainer>
  );
};

const CategoryList = ({
  visibleCategories,
  handleSelectCategory,
  activeCategoryId,
}: {
  handleSelectCategory: (categoryId: string) => void;
  visibleCategories: ContentCategory[];
  activeCategoryId?: string;
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [isLeftFilterArrowVisible, setShowLeftArrow] = useState(false);
  const [isRightFilterArrowVisible, setShowRightArrow] = useState(false);

  const handleScrollLeft = () => {
    const itemsToScroll = 2;

    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft - (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left: nextCheckpoint < 0 ? 0 : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  const handleScrollRight = () => {
    const itemsToScroll = 2;
    if (listRef.current) {
      const nodes = listRef.current.childNodes;
      const allNodes = Array.from(nodes);

      // We assume all nodes have the same width
      //@ts-ignore
      const nodeWidth = allNodes?.[0]?.clientWidth;
      const gap = 10;
      const nextCheckpoint =
        listRef.current.scrollLeft + (nodeWidth + gap) * itemsToScroll;

      listRef.current.scrollTo({
        left:
          nextCheckpoint > listRef.current.scrollWidth
            ? listRef.current.scrollWidth
            : nextCheckpoint,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // We have a scroll listener to always know the latest scroll position
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollHandler: EventListener = (ev: any) => {
      const scrollLeft = ev.target.scrollLeft;
      if (scrollLeft === 0) {
        setShowLeftArrow(false);
      } else {
        setShowLeftArrow(true);
      }

      if (scrollLeft + ev.target.clientWidth >= ev.target.scrollWidth - 15) {
        //10 as a buffer
        setShowRightArrow(false);
      } else {
        setShowRightArrow(true);
      }
    };
    const ref = listRef.current;
    ref?.addEventListener('scroll', scrollHandler);

    if (ref && ref.clientWidth < ref.scrollWidth) {
      // Upon mounting we check if we need to show the right arrow if there is a scroll
      setShowRightArrow(true);
    }

    return () => {
      if (ref) {
        ref.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [visibleCategories]);

  return (
    <FilterRow>
      {isLeftFilterArrowVisible && (
        <LeftArrowButtonContainer>
          <StyledArrowButton
            styles={{
              background: theme.colors.background,
              border: 'none',
              height: 35,
              width: 35,
              borderRadius: '50%',
            }}
            type="ghost"
            icon={<ArrowLeftSvg />}
            notext={true}
            onClick={() => {
              handleScrollLeft();
            }}
          />
        </LeftArrowButtonContainer>
      )}

      {visibleCategories.length > 0 && (
        <FilterButtonsContainer ref={listRef}>
          {visibleCategories.map((category, i) => (
            <FilterButton
              key={i}
              index={i}
              categoryId={category.id}
              handleSelectCategory={handleSelectCategory}
              icon={category.icon}
              categoryTitle={category.title}
              isActive={activeCategoryId === category.id}
            />
          ))}
        </FilterButtonsContainer>
      )}
      {isRightFilterArrowVisible && (
        <RightButtonContainer>
          <StyledArrowButton
            styles={{
              background: theme.colors.background,
              border: 'none',
              height: 35,
              width: 35,
              borderRadius: '50%',
            }}
            type="ghost"
            icon={<ArrowRightSvg />}
            notext={true}
            onClick={() => {
              handleScrollRight();
            }}
          />
        </RightButtonContainer>
      )}
    </FilterRow>
  );
};

const SearchInput = ({
  value,
  onChange,
}: {
  value?: string;
  onChange: (val?: string) => void;
}) => {
  const handleSearchValueChanged = (value?: string) => {
    onChange(value);
  };

  const placeholderText = [
    'Poem',
    'Infer the Country',
    'CCSS.ELA-LITERACY.W.6.1',
    'Dividing fractions',
    'Compare and contrast',
    'Photosynthesis',
    'Figurative language',
    'American Revolution',
    'Non-fiction writing',
    'Multiple Choice',
  ];

  const placeHolderText = useTypewriter(placeholderText, 75);

  return (
    <SearchInputContainer>
      <IconWrapper
        style={{
          position: 'absolute',
          left: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
        }}
        height="20px"
        width="20px"
        color="#5B5B5A"
      >
        <SearchSvg />
      </IconWrapper>
      <SearchInputComp
        placeholder={placeHolderText}
        value={value || ''}
        onChange={e => handleSearchValueChanged(e.target.value)}
      />
      {value && value.length > 0 && (
        <IconWrapper
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
          onClick={() => handleSearchValueChanged('')}
          height="15px"
          width="15px"
          color="#5B5B5A"
        >
          <CrossSvg />
        </IconWrapper>
      )}
    </SearchInputContainer>
  );
};

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  height: 52px;

  @media (max-width: 800px) {
    height: 32px;
  }
`;

const DebouncedSearchInput = ({
  onSearchInputChange,
  value,
}: {
  value?: string;
  onSearchInputChange: (val?: string) => void;
}) => {
  const router = useRouter();

  const [searchValue, setSearchValue] = useState<string | undefined>(
    router.query.searchPhrase as string,
  );

  useEffect(() => {
    // Support parent component clearing the value
    if (value === undefined || value === '') {
      setSearchValue(value);
    }
  }, [value]);

  const handleChange = (value?: string) => {
    setSearchValue(value);
    debounceSearchValueChange(value);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearchValueChange = useCallback(
    debounce((newSearchValue?: string) => {
      onSearchInputChange(newSearchValue);
    }, 400),
    [onSearchInputChange],
  );

  return <SearchInput value={searchValue} onChange={s => handleChange(s)} />;
};

const AuthenticatedHome = () => {
  // Prefetch a single generator page
  useRoutePrefetch(`/generators/[generatorName]/g/[generatorId]`);
  // Prefetch a single lesson designer page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`);
  // Prefetch a single lesson page
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]`);
  // Prefetch a single lesson result page
  useRoutePrefetch(`/[tenantId]/reports/[gameId]`);
  // Prefetch a browse by standard page
  useRoutePrefetch(`/[tenantId]/browseByStandard`);

  const isDesktop = useBreakPoint(800);
  const { tenantPlan } = usePaywallPrompt({
    startState: { screen: 'chooseProduct' },
  });
  const { tenantId } = useRouterMatch();
  const metrics = useMetrics();
  const animation = useAnimation();
  const hoverSequence = async () => {
    await animation.start({ scale: 1.03, transition: { duration: 0.1 } });
    animation.start({ scale: 1, transition: { duration: 0.1 } });
  };

  const [showSchoolSelector, setShowSchoolSelector] = useState(false);
  useEffect(() => {
    if (!tenantPlan) return;
    setShowSchoolSelector(!tenantPlan.isEnterprise() && !tenantPlan.country);
  }, [tenantPlan]);

  const [showTrialWelcomeMessage, setShowTrialWelcomeMessage] = useState(false);
  useEffect(() => {
    if (!tenantPlan) return;
    const trialWelcomeMessageShown = !!localStorage.getItem('trialWelcomeMessageShown');
    if (tenantPlan.isTrial() && !trialWelcomeMessageShown) {
      setShowTrialWelcomeMessage(true);
    }
  }, [tenantPlan]);

  const {
    subjects: selectedSubjects,
    grades: selectedGrades,
    clearFilters,
    activeCategory,
    handleGeneratorUrlSearchChange,
    searchState,
    visibleCategories,
  } = useGeneratorsQueryState();

  const handleSelectCategory = (categoryId: string) => {
    handleGeneratorUrlSearchChange({
      categoryToggled: categoryId as string,
    });
  };
  const handleSelectSubject = (selectedSubject: string) => {
    handleGeneratorUrlSearchChange({
      subjectToggled: selectedSubject,
    });
  };

  const handleSelectGrade = (selectedGrade: string) => {
    handleGeneratorUrlSearchChange({
      gradeToggled: selectedGrade,
    });
  };

  const handleClearFilters = () => {
    clearFilters();
  };

  const [scrollOffset, setScrollOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrollOffset(offset);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <NewAppLayout allowUnAuthenticated={true} scrollOffset={scrollOffset}>
      <PositionedContainer $isDesktop={isDesktop}>
        <TopSection $hasCategories={visibleCategories.length > 0}>
          {isDesktop && (
            <h1 style={{ color: theme.colors.white, fontSize: '36px' }}>
              What are you teaching next?
            </h1>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              color: theme.colors.white,
              fontSize: 16,
              position: 'relative',
              paddingBottom: isDesktop ? 20 : 12,
              width: '50%',
              minWidth: 300,
              maxWidth: 500,
            }}
          >
            <DebouncedSearchInput
              value={searchState.searchPhrase}
              onSearchInputChange={searchPhrase =>
                handleGeneratorUrlSearchChange({
                  newSearchPhrase: searchPhrase,
                })
              }
            />
            <DropdownContainer>
              <FilterWrapper>
                <AnimatePresence>
                  <motion.div animate={animation} onHoverStart={hoverSequence}>
                    <Link href={`/${tenantId}/browseByStandard`}>
                      <span
                        style={{ fontWeight: 600, display: 'flex', cursor: 'pointer' }}
                        onClick={() => {
                          metrics.logEvent(
                            'AuthenticatedHome.BrowseByStandards.Clicked',
                            {
                              tenantId,
                            },
                          );
                        }}
                      >
                        Standards
                        <IconWrapper
                          height={'25px'}
                          width={'25px'}
                          style={{ transform: 'rotate(90deg)' }}
                        >
                          <ArrowRightSvg />
                        </IconWrapper>
                      </span>
                    </Link>
                  </motion.div>
                </AnimatePresence>

                <HomepageFilterSelect
                  selectedItems={selectedSubjects}
                  selectableItems={subjectOptions}
                  onSelect={value => handleSelectSubject(value as string)}
                  label={'Subjects'}
                  singleSelect={true}
                />
                <div style={{ position: 'relative' }}>
                  <HomepageFilterSelect
                    selectedItems={selectedGrades}
                    selectableItems={CURIPOD_FILTER_GRADE_OPTIONS}
                    onSelect={value => handleSelectGrade(value as string)}
                    label={'Grades'}
                    singleSelect={false}
                  />
                  {(searchState.searchPhrase ||
                    selectedSubjects.length >= 1 ||
                    selectedGrades.length >= 1) && (
                    <ClearAllButton onClick={handleClearFilters} type="ghost">
                      Clear all filters
                    </ClearAllButton>
                  )}
                </div>
              </FilterWrapper>
            </DropdownContainer>
          </div>

          {visibleCategories && visibleCategories.length > 0 && (
            <CategoryList
              handleSelectCategory={handleSelectCategory}
              visibleCategories={visibleCategories}
              activeCategoryId={activeCategory?.id}
            />
          )}
        </TopSection>
        <GridSection>
          {(searchState.searchPhrase ||
            (!searchState.searchPhrase && !activeCategory) ||
            (!searchState.searchPhrase &&
              activeCategory?.subCategories.length === 0)) && (
            <>
              {activeCategory?.id === 'all' && (
                <SearchGeneratorsGrid
                  tenantPlan={tenantPlan}
                  searchState={{ categories: [], grades: [], subjects: [] }}
                />
              )}
              {activeCategory?.id !== 'all' && (
                <SearchGeneratorsGrid tenantPlan={tenantPlan} searchState={searchState} />
              )}
            </>
          )}
          {!searchState.searchPhrase &&
            activeCategory?.subCategories.map(subCategory => (
              <div key={subCategory.id}>
                {subCategory.type === 'generators' && (
                  <GeneratorsSlidingGrid
                    title={subCategory.title}
                    category={subCategory.id}
                    tenantPlan={tenantPlan}
                    searchState={searchState}
                  />
                )}
                {subCategory.type === 'custom_generators' && (
                  <CustomGeneratorsSlidingGrid title={subCategory.title} />
                )}
                {subCategory.type === 'latest_generators' && (
                  <LatestGeneratorsSlidingGrid title={subCategory.title} />
                )}
                {subCategory.type === 'lessons' && (
                  <LessonsSlidingGrid title={subCategory.title} />
                )}
                {subCategory.type === 'recent_generators' && (
                  <RecentGeneratorsSlidingGrid title={subCategory.title} />
                )}
                {subCategory.type === 'results' && (
                  <ResultsSlidingGrid title={subCategory.title} />
                )}
              </div>
            ))}
        </GridSection>
        {showSchoolSelector && <CountrySchoolSelectionPopup />}
        {showTrialWelcomeMessage && (
          <TrialWelcomeMessagePopup tenantPlan={tenantPlan} duration={10} />
        )}
      </PositionedContainer>
    </NewAppLayout>
  );
};

const SearchInputComp = styled.input`
  border-radius: 5px;
  background: #f3f3f1;
  outline: none;
  border: none;
  height: 100%;
  width: 100%;
  padding-left: 40px;
  font-weight: bold;
  color: #5b5b5a;
`;

const PositionedContainer = styled.div<{ $isDesktop: boolean }>`
  position: absolute;
  left: ${({ $isDesktop }) => ($isDesktop ? '240px' : '0px')};
  top: 60px;
  min-height: calc(100% - 60px);
  width: ${({ $isDesktop }) => ($isDesktop ? 'calc(100% - 240px)' : '100%')};

  ${({ $isDesktop }) =>
    !$isDesktop &&
    css`
      top: 0px;
      padding: 0px;
      padding-bottom: 60px;
    `}
`;

const TopSection = styled.div<{ $hasCategories?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.orange};
  border-radius: 10px;
  align-items: center;
  padding: 10px;
  margin: 20px 20px 25px 20px;

  ${({ $hasCategories }) =>
    !$hasCategories &&
    css`
      padding-bottom: 50px;
    `}

  background: radial-gradient(
      circle at 50px 50px,
      ${theme.colors.translucentWhite} 90px,
      transparent 91px
    ),
    radial-gradient(
      circle at calc(100% - 150px) 200px,
      ${theme.colors.translucentWhite} 90px,
      transparent 91px
    ),
    radial-gradient(
      circle at 200px calc(100% - 110px),
      ${theme.colors.translucentWhite} 90px,
      transparent 91px
    ),
    ${theme.colors.orange};
  h1 {
    padding: 20px 0px 0px 0px;
    font-size: 50px;
    text-align: center;
  }

  @media (max-width: 960px) {
    h1 {
      font-size: 36px;
    }
  }
  @media (max-width: 800px) {
    height: 200px;
    border-radius: 0px;
    justify-content: end;
    background: radial-gradient(
        circle at 50px -20px,
        ${theme.colors.translucentWhite} 90px,
        transparent 91px
      ),
      ${theme.colors.orange};
    box-shadow: 0px 4px 4px rgb(0, 0, 0, 0.1);
    margin: 0px 0px 20px 0px;
    padding-bottom: 2px;
  }
`;

const ArrowButtonContainer = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 0px;
  width: 0px;
  height: 102px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  width: 50px;
  @media (max-width: 800px) {
    height: 56px;
  }
`;

const LeftArrowButtonContainer = styled(ArrowButtonContainer)`
  left: 0px;
  background: linear-gradient(to left, rgba(249, 151, 70, 0), ${theme.colors.orange});
`;
const RightButtonContainer = styled(ArrowButtonContainer)`
  right: 0px;
  background: linear-gradient(to right, rgba(249, 151, 70, 0), ${theme.colors.orange});
`;

const GridSection = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownContainer = styled.div`
  display: flex;
  color: ${theme.colors.white};
  position: relative;
  justify-content: center;
  width: 100%;
`;

const FilterRow = styled.div`
  position: relative;
  display: flex;
  gap: 10px;
  max-width: 100%;
  width: fit-content;
  align-items: end;
  align-self: start;

  @media (max-width: 800px) {
    height: 50px;
  }
`;

const FilterButtonsContainer = styled.div`
  display: grid;
  border-radius: 10px;
  grid-template-columns: repeat(auto-fill, max-content);
  grid-auto-rows: 100px;
  grid-auto-flow: column;
  align-items: end;
  gap: 10px;
  color: ${theme.colors.white};
  font-size: 16px;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 3px;
  transition: all 1s;
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  @media (max-width: 800px) {
    grid-auto-rows: 50px;
    gap: 5px;
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-width: 100%;
  max-width: 100%;

  @media (max-width: 800px) {
    width: 100%;
    margin: 0 auto;
  }
`;

const StyledFilterButton = styled(Button)<{ $isActive: boolean }>`
  background-color: ${theme.colors.background};
  color: ${theme.colors.black};
  border: none;
  border-radius: 10px;
  font-weight: bold;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: calc(100% - 45px);
  flex-direction: column;
  width: 150px;
  padding: 8px 10px 5px 10px;
  margin-top: 30px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  span {
    display: inline-block;
    word-wrap: break-word;
    white-space: pre-line;
    z-index: 1;
  }

  svg {
    width: 28px;
    height: 28px;
    margin: 0px;
  }

  &:hover {
    background-color: ${theme.colors.white};
    color: ${theme.colors.black};
  }

  &:focus {
    background-color: ${theme.colors.white};
    color: ${theme.colors.black};
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      padding: 20px 20px 20px 20px;
      height: calc(100% - 30px);
      width: 170px;
    `}

  @media (max-width: 800px) {
    width: 80px;
    padding: 8px 2px 5px 2px;
    border-radius: 5px;
    height: calc(100% - 15px);

    ${({ $isActive }) =>
      $isActive &&
      css`
        height: calc(100% - 10px);
      `}

    span {
      font-size: 10px;
      word-wrap: break-word;
      white-space: pre-line;
      line-height: 12px;
    }
    svg {
      width: 18px;
      height: 18px;
      margin: 0px;
    }
  }
`;

const ButtonIconWrapper = styled.div`
  position: absolute;
  top: -28px;
  right: calc(50% - 22.5px));
  display: flex;
  justify-content: center;
  align-items: center;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: inherit;

  @media (max-width: 800px) {
  width: 25px;
  height: 25px;
  top: -15px;
  }
`;

const ClearAllButton = styled(Button)`
  border: none;
  margin-left: 10px;
  position: absolute;
  right: -100%;
  top: 5px;

  color: ${theme.colors.white};
  box-shadow: none;
  padding: 0;
  span {
    text-decoration: underline;
    font-size: 14px;

    &:hover {
      text-decoration: none;
    }
  }
  &:hover,
  &:focus {
    background: transparent;
    color: ${theme.colors.white};
  }

  @media (max-width: 800px) {
    top: 25px;
    right: calc(50% - 45px);
  }
`;

export default AuthenticatedHome;
